import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { RematchingContainer } from '../components/RematchingContainer';
import { Form, Formik } from 'formik';
import { FormFooter } from '../components/FormFooter';
import { NewAddressNextData } from '../state-machine/machine';
import { AddressVerificationProvider, settingsAddressValidation, StandaloneAddressQuickSearchForm, useWithAddressVerify } from '@uplift/ui-v2';
import { AddressType } from '@uplift/types';
import * as Yup from 'yup';
import type { L10NShape } from '@uplift/services';
import { useL10n } from '@uplift/hooks';

export type RematchingNewAddressPageProps = {
  onBack: () => void;
  onNext: (data: NewAddressNextData) => void;
  newAddress: AddressType | null;
};

type NewAddressFormType = {
  address: AddressType;
};

export const newAddressValidation = (l10n: L10NShape): Yup.ObjectSchema<NewAddressFormType> =>
  Yup.object({
    address: settingsAddressValidation(l10n),
  }) as Yup.ObjectSchema<NewAddressFormType>;

type RematchingNewAddressPageContentProps = RematchingNewAddressPageProps & {
  initialValues: NewAddressFormType;
};

const RematchingNewAddressPageContent: React.VFC<RematchingNewAddressPageContentProps> = (props) => {
  const { classes } = useNewAddressPageContentStyles();
  const l10n = useL10n();
  const withAddressVerify = useWithAddressVerify();

  const onSubmit = withAddressVerify(async (values: NewAddressNextData) => {
    props.onNext(values);
  }, {
    shouldCheck: true,
    initialAddress: props.initialValues.address,
    getAddressFromFormikValues: (values: NewAddressFormType) => values.address,
  });

  return (
    <RematchingContainer>
      <Typography variant="h3">Where did you move to?</Typography>
      <Typography className={classes.description}>We save your home address in case of an emergency. When you’ve finished scheduling a session with a new therapist, we’ll update your profile with your new address.</Typography>
      <Formik
        initialValues={props.initialValues}
        validateOnMount
        validationSchema={newAddressValidation(l10n)}
        onSubmit={onSubmit}
      >
        {(formik) => (
          <Form>
            <Box mt={4} mb={6} maxWidth="400px">
              <StandaloneAddressQuickSearchForm
                label="New Home Address"
                getFieldName={(key) => `address.${key}`}
              />
            </Box>
            <FormFooter
              nextDisabled={!formik.isValid}
              onBack={props.onBack}
              onNext={formik.handleSubmit}
            />
          </Form>
        )}
      </Formik>
    </RematchingContainer>
  );
};

const useNewAddressPageContentStyles = makeStyles({ name: 'NewAddressPageContent' })(theme => ({
  description: {
    marginTop: theme.spacing(1),
  },
}));

export const RematchingNewAddressPage: React.VFC<RematchingNewAddressPageProps> = (props) => {

  const initialValues: NewAddressFormType = {
    address: props.newAddress ?? {
      address1: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
    }
  };

  return (
    <AddressVerificationProvider
      shouldReverifyAddress={false}
      parsedVerifiedAddress={initialValues.address.address1 ? initialValues.address : undefined}
    >
      <RematchingNewAddressPageContent {...props} initialValues={initialValues} />
    </AddressVerificationProvider>
  );
};
